import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import SearchableSelect from "../SearchableSelect";
import { tableRows } from "../../utils/xlsx";
import { AsOfFilter, cellRight, download, EmptyState, ExportButton, LoadingOverlay, money, REPORTS, sheetHeading, StatCard, todayIso } from "./common";

const KINDS = {
  receivable: {
    endpoint: "receivables",
    title: "Receivables",
    party: "Customer",
    partyParam: "customerId",
    partiesPath: "/api/v1/customers",
    subtitle:
      "What customers owe, bill by bill: bill total − collected − credit notes + debit notes and charges. A bill is overdue once the bill date plus the customer's credit days has passed.",
  },
  payable: {
    endpoint: "payables",
    title: "Payables",
    party: "Supplier",
    partyParam: "brandId",
    partiesPath: "/api/v1/brands",
    subtitle:
      "What you owe suppliers, bill by bill: verified purchase bill total − payments allocated to it. A bill is overdue once the bill date plus the supplier ledger's credit days has passed.",
  },
};

const BUCKETS = ["Not due", "1-30 days", "31-60 days", "61-90 days", "Above 90 days"];

/** Outstanding and overdue receivables or payables with ageing, party-wise and bill-wise. */
const OutstandingAgeing = ({ kind }) => {
  const config = KINDS[kind];
  const [asOfDate, setAsOfDate] = useState(todayIso);
  const [parties, setParties] = useState([]);
  const [partyId, setPartyId] = useState("");
  const [view, setView] = useState("party");
  const [onlyOverdue, setOnlyOverdue] = useState(false);
  const [bucket, setBucket] = useState("");
  const [search, setSearch] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api
      .get(config.partiesPath, { size: 1000 })
      .then((data) => setParties(data?.content || (Array.isArray(data) ? data : [])))
      .catch(() => {});
  }, [config.partiesPath]);

  const load = async () => {
    if (!asOfDate) {
      toast.error("Select a date");
      return;
    }
    setLoading(true);
    try {
      setReport(await api.get(`${REPORTS}/${config.endpoint}`, { asOfDate, [config.partyParam]: partyId || undefined, includeBills: true }));
    } catch (error) {
      toast.error(error.message || `Failed to load ${config.title.toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setReport(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  const needle = search.trim().toLowerCase();
  const partyRows = useMemo(
    () =>
      (report?.parties || []).filter(
        (row) => (!onlyOverdue || Number(row.overdue) > 0) && (!needle || (row.partyName || "").toLowerCase().includes(needle))
      ),
    [report, onlyOverdue, needle]
  );
  const billRows = useMemo(
    () =>
      (report?.bills || []).filter(
        (row) =>
          (!onlyOverdue || row.overdue) &&
          (!bucket || row.bucket === bucket) &&
          (!needle || (row.partyName || "").toLowerCase().includes(needle) || (row.documentNumber || "").toLowerCase().includes(needle))
      ),
    [report, onlyOverdue, bucket, needle]
  );

  const partyColumns = [
    { label: config.party, key: "partyName" },
    { label: "Credit Days", key: "creditDays", format: "int" },
    ...(kind === "receivable" ? [{ label: "Credit Limit", key: "creditLimit", format: "money", total: false }] : []),
    { label: "Bills", key: "billCount", format: "int" },
    { label: "Outstanding", key: "outstanding", format: "money" },
    { label: "Overdue", key: "overdue", format: "money" },
    { label: "Overdue Bills", key: "overdueBillCount", format: "int" },
    { label: "Oldest Overdue (days)", key: "oldestOverdueDays", format: "int" },
    { label: "Ledger Balance", key: "ledgerBalance", format: "money" },
    { label: "On Account / Unallocated", key: "onAccount", format: "money" },
  ];
  const billColumns = [
    { label: "Bill No", key: "documentNumber" },
    { label: config.party, key: "partyName" },
    { label: "Bill Date", key: "billDate" },
    { label: "Due Date", key: "dueDate" },
    { label: "Bill Amount", key: "billAmount", format: "money" },
    { label: "Settled", key: "settled", format: "money" },
    { label: "Outstanding", key: "outstanding", format: "money" },
    { label: "Days Overdue", key: "daysOverdue", format: "int" },
    { label: "Ageing", key: "bucket" },
  ];

  const exportExcel = () => {
    const heading = sheetHeading(`${config.title} Ageing`, `As on ${report.asOfDate}`);
    download(`${config.title} ${report.asOfDate}`, [
      { name: `${config.party}-wise`, rows: [...heading, ...tableRows(partyColumns, partyRows, { totals: true })] },
      { name: "Bill-wise", rows: [...heading, ...tableRows(billColumns, billRows, { totals: true })] },
      {
        name: "Ageing",
        rows: [
          ...heading,
          ...tableRows(
            [
              { label: "Ageing", key: "label" },
              { label: "Bills", key: "billCount", format: "int" },
              { label: "Amount", key: "amount", format: "money" },
            ],
            report.ageing,
            { totals: true }
          ),
        ],
        widths: [18, 10, 16],
      },
    ]);
  };

  const cell = (column, row) => {
    const value = row[column.key];
    if (value === null || value === undefined || value === "") return "-";
    return column.format === "money" ? money(value) : value;
  };
  const columns = view === "party" ? partyColumns : billColumns;
  const rows = view === "party" ? partyRows : billRows;

  return (
    <div className="p-4" style={{ position: "relative" }}>
      <LoadingOverlay show={loading} />
      <div className="page-subtitle" style={{ marginBottom: "12px" }}>{config.subtitle}</div>
      <div className="filters-bar">
        <AsOfFilter value={asOfDate} onChange={setAsOfDate} />
        <SearchableSelect
          value={partyId}
          onChange={setPartyId}
          options={[{ value: "", label: `All ${config.party.toLowerCase()}s` }, ...parties.map((p) => ({ value: p.id, label: p.name }))]}
          placeholder={`All ${config.party.toLowerCase()}s`}
          style={{ minWidth: "220px" }}
        />
        <button className="primary" onClick={load} disabled={loading}>
          {loading ? "Loading..." : "Run Report"}
        </button>
        <ExportButton onClick={exportExcel} disabled={!report} />
      </div>

      {!report ? (
        <EmptyState>{loading ? "Loading..." : "No data loaded"}</EmptyState>
      ) : (
        <>
          <div className="stat-grid" style={{ marginTop: "16px" }}>
            <StatCard label={`Total ${config.title}`} value={`₹${money(report.totalOutstanding)}`} hint={`${report.billCount} open bills`} />
            <StatCard
              label={`Overdue ${config.title}`}
              value={`₹${money(report.overdueAmount)}`}
              hint={`${report.overdueBillCount} overdue bills`}
              tone={Number(report.overdueAmount) > 0 ? "bad" : undefined}
            />
            <StatCard label="Not Yet Due" value={`₹${money(report.notDueAmount)}`} />
            <StatCard label="Balance as per Books" value={`₹${money(report.ledgerBalance)}`} hint="Ledger balances of the parties listed" />
          </div>

          <div className="table-wrapper" style={{ marginTop: 0 }}>
            <table>
              <thead>
                <tr>
                  {report.ageing.map((b) => (
                    <th key={b.label}>{b.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {report.ageing.map((b) => (
                    <td key={b.label} style={{ textAlign: "center" }}>
                      <div style={{ fontWeight: 600 }}>₹{money(b.amount)}</div>
                      <div className="page-subtitle">{b.billCount} bills</div>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          <div className="filters-bar" style={{ marginTop: "16px" }}>
            <button className={view === "party" ? "primary" : "secondary"} onClick={() => setView("party")}>
              {config.party}-wise
            </button>
            <button className={view === "bill" ? "primary" : "secondary"} onClick={() => setView("bill")}>
              Bill-wise
            </button>
            <input type="text" placeholder={`Search ${config.party.toLowerCase()}${view === "bill" ? " or bill no" : ""}`} value={search} onChange={(e) => setSearch(e.target.value)} />
            {view === "bill" ? (
              <select value={bucket} onChange={(e) => setBucket(e.target.value)} aria-label="Ageing bucket">
                <option value="">All ageing</option>
                {BUCKETS.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            ) : null}
            <label style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "13px" }}>
              <input type="checkbox" checked={onlyOverdue} onChange={(e) => setOnlyOverdue(e.target.checked)} />
              Overdue only
            </label>
          </div>

          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th key={column.key} style={column.format === "money" ? cellRight : undefined}>
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length}>
                      <EmptyState>Nothing outstanding{onlyOverdue || bucket || needle ? " for these filters" : ""}</EmptyState>
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr key={view === "party" ? row.partyId : row.documentId}>
                      {columns.map((column) => (
                        <td
                          key={column.key}
                          style={{
                            ...(column.format === "money" ? cellRight : {}),
                            ...((column.key === "overdue" && Number(row.overdue) > 0) || (column.key === "daysOverdue" && row.overdue)
                              ? { color: "var(--color-danger)", fontWeight: 600 }
                              : {}),
                          }}
                        >
                          {cell(column, row)}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
              {rows.length ? (
                <tfoot>
                  <tr>
                    {columns.map((column, index) => (
                      <td key={column.key} style={{ fontWeight: 700, ...(column.format === "money" ? cellRight : {}) }}>
                        {index === 0
                          ? "Total"
                          : column.format === "money" && column.total !== false
                          ? money(rows.reduce((sum, row) => sum + Number(row[column.key] || 0), 0))
                          : ""}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              ) : null}
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default OutstandingAgeing;
